import type {Mapper} from '@automapper/core';
import {type DownloadColumn, type IDownloadByLinkService, ServerResponseStatus} from '@igp/shared/api';
import type {DebtTransferLog} from '@igp/shared/contract/pam';
import {map} from '@igp/shared/otel';
import {
    type DownloadStrategyRequest,
    type DownloadStrategyResponse,
    EntityDownloadClientStrategy,
    type IDownloadStrategy,
} from '@igp/shared/redux/download';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {
    DebtTransferLogDownloadFieldsTypeName,
    DebtTransferLogDownloadModelTypeName,
    DebtTransferLogQueryFieldsTypeName,
    DebtTransferLogServiceTypes,
    DebtTransferLogTypeName,
    DebtTransferLogViewModelKeysTypeName,
    lastUpdatedItemId,
} from './constants';
import type {DebtTransferLogService} from './service';
import type {DebtTransferLogDownloadFields, DebtTransferLogEntityType, DebtTransferLogViewModelKeys} from './types';

@injectable()
export class DebtTransferLogDownloadClientStrategy extends EntityDownloadClientStrategy<
    DebtTransferLogViewModelKeys,
    DebtTransferLogEntityType
> {
    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(DebtTransferLogServiceTypes.service) service: DebtTransferLogService
    ) {
        super(mapper, service, 'DebtTransferLog', {
            viewModelKeys: DebtTransferLogViewModelKeysTypeName,
            queryFields: DebtTransferLogQueryFieldsTypeName,
            model: DebtTransferLogTypeName,
            downloadModel: DebtTransferLogDownloadModelTypeName,
        });
    }

    protected filterItems(items: DebtTransferLog[]): DebtTransferLog[] {
        return items.filter(i => i.log_id !== lastUpdatedItemId);
    }
}

@injectable()
export class DebtTransferLogDownloadServerStrategy implements IDownloadStrategy<DebtTransferLogViewModelKeys> {
    private readonly _mapper: Mapper;
    private readonly _service: IDownloadByLinkService<DebtTransferLogDownloadFields>;

    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(DebtTransferLogServiceTypes.service)
        service: IDownloadByLinkService<DebtTransferLogDownloadFields>
    ) {
        this._mapper = mapper;
        this._service = service;
    }

    process(
        request: DownloadStrategyRequest<DebtTransferLogViewModelKeys>
    ): Observable<DownloadStrategyResponse<DebtTransferLogViewModelKeys>> {
        const columns: DownloadColumn<DebtTransferLogDownloadFields>[] = request.keys?.map<DownloadColumn<DebtTransferLogDownloadFields>>(
            k => {
                const queryField = this._mapper.map<DebtTransferLogViewModelKeys, DebtTransferLogDownloadFields>(
                    k,
                    DebtTransferLogViewModelKeysTypeName,
                    DebtTransferLogDownloadFieldsTypeName
                );
                return {
                    name: request.headers[k] ?? k,
                    field: queryField,
                };
            }
        );
        return this._service
            .getDownloadLink({
                filter: request.filter,
                columns,
                report_name: request.filename,
            })
            .pipe(
                map(response => ({
                    status: response?.status === ServerResponseStatus.Success ? 'Success' : 'Failed',
                    link: response?.responsePayload,
                    mode: 'server',
                }))
            );
    }
}
